/**
 * Optimized React Query configurations for different data types
 * Based on data volatility and usage patterns
 */

import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

// Base configuration types
type QueryConfig<T = unknown> = Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>;
type MutationConfig<T = unknown, U = unknown> = Omit<UseMutationOptions<T, Error, U>, 'mutationFn'>;

/**
 * Configuration for different data categories based on volatility and criticality
 */
export const queryConfigs = {
  /**
   * STATIC DATA - Rarely changes, can be cached aggressively
   * Examples: Commodities, Ports, Suppliers, Buyers
   */
  staticData: {
    staleTime: 1000 * 60 * 30, // 30 minutes
    cacheTime: 1000 * 60 * 60 * 2, // 2 hours
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  } as QueryConfig,

  /**
   * REFERENCE DATA - Changes occasionally, moderate caching
   * Examples: User profiles, Company settings, API configurations
   */
  referenceData: {
    staleTime: 1000 * 60 * 10, // 10 minutes
    cacheTime: 1000 * 60 * 45, // 45 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  } as QueryConfig,

  /**
   * BUSINESS DATA - Changes regularly, balanced caching
   * Examples: Contracts, Shipments (non-tracking), Financial summaries
   */
  businessData: {
    staleTime: 1000 * 60 * 3, // 3 minutes
    cacheTime: 1000 * 60 * 15, // 15 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  } as QueryConfig,

  /**
   * REAL-TIME DATA - Changes frequently, minimal caching
   * Examples: Container tracking, Live shipment status, Health monitoring
   */
  realTimeData: {
    staleTime: 1000 * 30, // 30 seconds
    cacheTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: 1000 * 60, // Auto-refresh every minute
  } as QueryConfig,

  /**
   * CRITICAL REAL-TIME - Needs immediate updates
   * Examples: System health, Alert notifications, Emergency status
   */
  criticalRealTime: {
    staleTime: 1000 * 15, // 15 seconds
    cacheTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: 1000 * 30, // Auto-refresh every 30 seconds
  } as QueryConfig,

  /**
   * SEARCH RESULTS - Short-lived, context-dependent
   * Examples: Contract search, Supplier search, Document search
   */
  searchResults: {
    staleTime: 1000 * 60 * 1, // 1 minute
    cacheTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  } as QueryConfig,

  /**
   * DASHBOARD DATA - Balanced between freshness and performance
   * Examples: Dashboard cards, Summary statistics, Recent activities
   */
  dashboardData: {
    staleTime: 1000 * 60 * 2, // 2 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  } as QueryConfig,
};

/**
 * Mutation configurations for different operation types
 */
export const mutationConfigs = {
  /**
   * CREATE operations - Generally safe to retry
   */
  create: {
    retry: 1,
    retryDelay: 1000,
  } as MutationConfig,

  /**
   * UPDATE operations - Idempotent, safe to retry
   */
  update: {
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  } as MutationConfig,

  /**
   * DELETE operations - Should not be retried automatically
   */
  delete: {
    retry: 0,
  } as MutationConfig,

  /**
   * CRITICAL operations - No automatic retry, manual confirmation
   */
  critical: {
    retry: 0,
  } as MutationConfig,

  /**
   * BULK operations - Longer timeout, single retry
   */
  bulk: {
    retry: 1,
    retryDelay: 3000,
  } as MutationConfig,
};

/**
 * Query key factories for consistent cache management
 */
export const queryKeys = {
  // Auth and user data
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    permissions: () => [...queryKeys.auth.all, 'permissions'] as const,
  },

  // Contracts
  contracts: {
    all: ['contracts'] as const,
    lists: () => [...queryKeys.contracts.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.contracts.lists(), { filters }] as const,
    details: () => [...queryKeys.contracts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contracts.details(), id] as const,
  },

  // Shipments
  shipments: {
    all: ['shipments'] as const,
    lists: () => [...queryKeys.shipments.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.shipments.lists(), { filters }] as const,
    details: () => [...queryKeys.shipments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.shipments.details(), id] as const,
  },

  // Containers and tracking
  containers: {
    all: ['containers'] as const,
    lists: () => [...queryKeys.containers.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.containers.lists(), { filters }] as const,
    tracking: (id: string) => [...queryKeys.containers.all, 'tracking', id] as const,
  },

  // Tracking-specific queries
  tracking: {
    all: ['tracking'] as const,
    containers: () => [...queryKeys.tracking.all, 'containers'] as const,
    containersList: (filters: Record<string, any>) => [...queryKeys.tracking.containers(), { filters }] as const,
    filters: () => [...queryKeys.tracking.all, 'filters'] as const,
  },

  // Financial data
  financial: {
    all: ['financial'] as const,
    summary: (period: string) => [...queryKeys.financial.all, 'summary', period] as const,
    commissions: (period: string, status?: string) => 
      [...queryKeys.financial.all, 'commissions', period, status] as const,
    revenue: (period: string) => [...queryKeys.financial.all, 'revenue', period] as const,
  },

  // Health monitoring
  health: {
    all: ['health'] as const,
    status: () => [...queryKeys.health.all, 'status'] as const,
    integration: (name: string) => [...queryKeys.health.all, 'integration', name] as const,
    uptime: (name: string, period: string) => [...queryKeys.health.all, 'uptime', name, period] as const,
  },

  // Static reference data
  reference: {
    all: ['reference'] as const,
    suppliers: () => [...queryKeys.reference.all, 'suppliers'] as const,
    buyers: () => [...queryKeys.reference.all, 'buyers'] as const,
    ports: () => [...queryKeys.reference.all, 'ports'] as const,
    commodities: () => [...queryKeys.reference.all, 'commodities'] as const,
    vessels: () => [...queryKeys.reference.all, 'vessels'] as const,
  },
};

/**
 * Utility functions for cache invalidation
 */
export const cacheUtils = {
  /**
   * Invalidate all queries related to a contract
   */
  invalidateContract: (queryClient: any, contractId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.contracts.detail(contractId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.contracts.lists() });
    queryClient.invalidateQueries({ queryKey: queryKeys.financial.all });
  },

  /**
   * Invalidate all queries related to a shipment
   */
  invalidateShipment: (queryClient: any, shipmentId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.shipments.detail(shipmentId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.shipments.lists() });
    queryClient.invalidateQueries({ queryKey: queryKeys.containers.lists() });
  },

  /**
   * Invalidate financial data
   */
  invalidateFinancial: (queryClient: any) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.financial.all });
  },

  /**
   * Invalidate reference data (use sparingly)
   */
  invalidateReference: (queryClient: any) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.reference.all });
  },

  /**
   * Clear all cache (nuclear option)
   */
  clearAll: (queryClient: any) => {
    queryClient.clear();
  },
};

/**
 * Prefetch strategies for improved UX
 */
export const prefetchStrategies = {
  /**
   * Prefetch contract details when hovering over list item
   */
  contractDetails: (queryClient: any, contractId: string, fetchFn: () => Promise<any>) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.contracts.detail(contractId),
      queryFn: fetchFn,
      ...queryConfigs.businessData,
    });
  },

  /**
   * Prefetch related shipments when viewing contract
   */
  contractShipments: (queryClient: any, contractId: string, fetchFn: () => Promise<any>) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.shipments.list({ contractId }),
      queryFn: fetchFn,
      ...queryConfigs.businessData,
    });
  },

  /**
   * Prefetch reference data on app startup
   */
  essentialReference: (queryClient: any, fetchFunctions: Record<string, () => Promise<any>>) => {
    // Prefetch commonly used reference data
    Object.entries(fetchFunctions).forEach(([key, fetchFn]) => {
      const queryKey = (queryKeys.reference as any)[key]?.();
      if (queryKey) {
        queryClient.prefetchQuery({
          queryKey,
          queryFn: fetchFn,
          ...queryConfigs.staticData,
        });
      }
    });
  },
};

/**
 * Error boundary helpers for query errors
 */
export const errorHandlers = {
  /**
   * Standard error handler for queries
   */
  queryError: (error: any) => {
    console.error('Query Error:', error);
    
    // Handle specific error types
    if (error?.response?.status === 401) {
      // Redirect to login or refresh token
      console.log('Authentication error - redirect to login');
    } else if (error?.response?.status === 403) {
      // Show permission denied message
      console.log('Permission denied');
    } else if (error?.response?.status >= 500) {
      // Show server error message
      console.log('Server error - please try again later');
    }
  },

  /**
   * Standard error handler for mutations
   */
  mutationError: (error: any) => {
    console.error('Mutation Error:', error);
    
    // Show user-friendly error message
    return error?.response?.data?.message || 'An error occurred. Please try again.';
  },
};